import type { AuctisyncSupabase } from './client.js';

/**
 * Lot photographs for public surfaces.
 *
 * WHY THIS IS A SEPARATE FETCH rather than an embedded select on the lot query:
 * `PUBLIC_LOT_COLUMNS` is a deny-by-default list with a documented reason to
 * stay exactly as it is (a `select('*')` there fails outright for a signed-out
 * visitor, so every column is granted deliberately). Embedding a related
 * resource changes the returned row shape and would ripple through
 * `PublicLotRow`, which exists to make reaching for a reserve price a COMPILE
 * error. Photographs are not worth weakening that.
 *
 * HISTORY. Until 2026-08-10 no buyer could see a lot photograph at all, on the
 * website or in the app. `inventory_photos` had RLS on and SELECT granted to
 * anon/authenticated but NO policy admitting either role, and a grant without a
 * policy returns zero rows. The website queried the table and rendered an empty
 * carousel; the native app never asked for photographs in the first place, which
 * hid the same defect a second way. The policy landed in
 * `20260810170000_inventory_photos_public_select.sql`; this module is the client
 * half.
 */

/** One photograph, already resolved to a URL the client can render. */
export interface LotPhoto {
  url: string;
  sortOrder: number;
}

/** Photographs per lot id, each list ordered by `sort_order`. */
export type LotPhotoMap = ReadonlyMap<string, LotPhoto[]>;

/**
 * The two ways a photograph reaches a lot. BOTH are needed and neither is
 * sufficient alone:
 *
 *  - `lot_id` — a photograph attached to the lot itself. This is the normal
 *    path: a trigger copies an inventory item's photographs onto the lot when
 *    the lot is created, so most lots carry their own rows.
 *  - `inventory_item_id` — the photograph still lives on the source item and
 *    was never copied. Older inventory predates the copying trigger.
 *
 * Querying only `lot_id` silently loses photographs for older stock, and
 * querying only `inventory_item_id` loses every photograph attached straight to
 * a lot.
 */
export interface LotPhotoTarget {
  id: string;
  inventory_item_id?: string | null;
}

interface PhotoRow {
  lot_id: string | null;
  inventory_item_id: string | null;
  display_url: string | null;
  storage_path: string | null;
  sort_order: number | null;
}

/** `display_url` is what the uploader writes; `storage_path` is the fallback. */
function urlOf(row: PhotoRow): string | null {
  const url = row.display_url?.trim();
  return url !== undefined && url !== '' ? url : null;
}

function push(into: Map<string, LotPhoto[]>, lotId: string, photo: LotPhoto): void {
  const list = into.get(lotId);
  if (list === undefined) into.set(lotId, [photo]);
  else list.push(photo);
}

/**
 * Fetch photographs for a set of lots.
 *
 * Returns an empty map for an empty input WITHOUT a round trip — a `.in()` on
 * an empty array is a query that can only return nothing, and browse screens
 * render before their lots arrive.
 *
 * Never throws for a missing photograph: a lot with no picture is ordinary, not
 * an error, and a screen that fails to render because an image is absent is
 * worse than one showing a placeholder.
 */
export async function fetchLotPhotos(
  supabase: AuctisyncSupabase,
  lots: readonly LotPhotoTarget[],
): Promise<LotPhotoMap> {
  const byId = new Map<string, LotPhoto[]>();
  if (lots.length === 0) return byId;

  const lotIds = lots.map((l) => l.id);
  const itemIds = lots
    .map((l) => l.inventory_item_id)
    .filter((id): id is string => typeof id === 'string' && id !== '');

  // Which lots does a given inventory item feed? An item can back more than one
  // lot (the same stock relisted), so this is a list, not a single id.
  const lotsByItem = new Map<string, string[]>();
  for (const lot of lots) {
    const item = lot.inventory_item_id;
    if (typeof item !== 'string' || item === '') continue;
    const existing = lotsByItem.get(item);
    if (existing === undefined) lotsByItem.set(item, [lot.id]);
    else existing.push(lot.id);
  }

  const queries = [
    supabase
      .from('inventory_photos')
      .select('lot_id, inventory_item_id, display_url, storage_path, sort_order')
      .in('lot_id', lotIds)
      .order('sort_order', { ascending: true }),
  ];
  if (itemIds.length > 0) {
    queries.push(
      supabase
        .from('inventory_photos')
        .select('lot_id, inventory_item_id, display_url, storage_path, sort_order')
        .is('lot_id', null)
        .in('inventory_item_id', itemIds)
        .order('sort_order', { ascending: true }),
    );
  }

  const results = await Promise.all(queries);

  const seen = new Set<string>();
  for (const { data, error } of results) {
    if (error !== null) continue;
    for (const row of (data ?? []) as PhotoRow[]) {
      const url = urlOf(row);
      if (url === null) continue;
      const sortOrder = row.sort_order ?? 0;

      // A photograph copied onto a lot AND still present on its item would
      // otherwise appear twice in the same carousel.
      const targets =
        row.lot_id !== null ? [row.lot_id] : (lotsByItem.get(row.inventory_item_id ?? '') ?? []);

      for (const lotId of targets) {
        const key = `${lotId} ${url}`;
        if (seen.has(key)) continue;
        seen.add(key);
        push(byId, lotId, { url, sortOrder });
      }
    }
  }

  for (const list of byId.values()) list.sort((a, b) => a.sortOrder - b.sortOrder);
  return byId;
}

/** The image to show when there is room for exactly one. */
export function primaryPhotoUrl(photos: LotPhotoMap, lotId: string): string | null {
  return photos.get(lotId)?.[0]?.url ?? null;
}
